import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Copy,
  LibraryBig,
  Loader2,
  Maximize2,
  Minimize2,
  Redo2,
  Save,
  Scissors,
  Trash2,
  Undo2,
  UploadCloud,
  VolumeX,
} from "lucide-react";
import {
  isIdentityTimeline,
  type Clip,
  type DraftChapter,
  type Timeline as TimelineDoc,
} from "@rec/shared";
import { api } from "@/lib/api";
import {
  appendClip,
  duplicateClip,
  moveClip,
  pushHistory,
  setClipLabel,
  trimClip,
  redo as redoOp,
  removeClip,
  setTransition,
  splitAt,
  toggleMute,
  totalDurationMs,
  undo as undoOp,
  type History,
} from "@/lib/timeline";
import { toast } from "@/components/Toaster";
import { StudioPreview, type StudioPreviewHandle } from "@/components/studio/StudioPreview";
import { LibraryPicker } from "@/components/LibraryPicker";

import { ElementList } from "@/components/studio/ElementList";
import { Timeline } from "@/components/studio/Timeline";
import { ChapterEditor } from "@/components/studio/ChapterEditor";
import { PublishDialog } from "@/components/studio/PublishDialog";
import { UploadButton } from "@/components/UploadButton";
import { ImportLinkButton } from "@/components/ImportLinkDialog";
import { InlineRename } from "@/components/InlineRename";
import { Button, Card, ErrorState, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";

/**
 * The editor: preview on top, timeline underneath, sources and chapters beside.
 *
 * All edit state is local and explicit-save (Ctrl/Cmd-S or the button), with a
 * dirty marker and a leave warning — the same contract as every serious editor,
 * and the same one the chapter drafts promised: no autosave surprises, no
 * half-states on the server.
 *
 * Undo is one step per human gesture. The timeline commits once per completed
 * drag, so history never fills with per-pixel noise.
 */

type SidePanel = "sources" | "chapters";

export function StudioEditor() {
  const { projectId } = useParams({ from: "/studio/$projectId" });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const previewRef = useRef<StudioPreviewHandle>(null);
  const { t } = useTranslation(["studio", "common"]);

  const project = useQuery({
    queryKey: ["studio-project", projectId],
    queryFn: () => api.getStudioProject(projectId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // ── Editor state, seeded once from the loaded draft ────────────────────────
  const [title, setTitle] = useState("");
  const [clips, setClips] = useState<Clip[]>([]);
  const [chapters, setChapters] = useState<DraftChapter[]>([]);
  const [history, setHistory] = useState<History>({ past: [], future: [] });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [playheadMs, setPlayheadMs] = useState(0);
  const [panel, setPanel] = useState<SidePanel>("sources");
  const [publishing, setPublishing] = useState(false);
  const [savedFingerprint, setSavedFingerprint] = useState("");
  /**
   * Fit the whole editor to the monitor instead of scrolling. A preference,
   * remembered per device — some people want the big preview, some want to
   * see the timeline without reaching for the scrollbar.
   */
  const [fitScreen, setFitScreen] = useState(
    () => localStorage.getItem("studio.fitScreen") === "1",
  );
  /** Per-project display names for sources, saved with the draft. */
  const [sourceLabels, setSourceLabels] = useState<Record<string, string>>({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const seeded = useRef(false);

  useEffect(() => {
    if (!project.data || seeded.current) return;
    seeded.current = true;
    setTitle(project.data.title);
    setClips(project.data.timeline.clips);
    setChapters(project.data.draftChapters);
    setSourceLabels(project.data.sourceLabels);
    setSavedFingerprint(
      JSON.stringify({
        t: project.data.title,
        c: project.data.timeline.clips,
        ch: project.data.draftChapters,
        sl: project.data.sourceLabels,
      }),
    );
  }, [project.data]);

  const fingerprint = JSON.stringify({
    t: title,
    c: clips,
    ch: chapters,
    sl: sourceLabels,
  });
  const dirty = seeded.current && fingerprint !== savedFingerprint;

  // Leaving with unsaved work deserves exactly one warning, from the browser.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // ── Source media, one query per referenced recording ───────────────────────
  const sourceIds = useMemo(
    () => [...new Set(clips.map((c) => c.recordingId))],
    [clips],
  );

  const sourceQueries = useQueries({
    queries: sourceIds.map((id) => ({
      queryKey: ["recording", id],
      queryFn: () => api.getRecording(id),
      staleTime: 30 * 60_000,
    })),
  });

  const sources = useMemo(() => {
    const media = new Map<string, { mediaUrl: string | null; mediaType: "hls" | "mp4" }>();
    const durations = new Map<string, number>();
    const titles = new Map<string, string>();
    const thumbnails = new Map<string, string | null>();
    sourceQueries.forEach((query, i) => {
      const id = sourceIds[i]!;
      if (query.data) {
        media.set(id, { mediaUrl: query.data.mediaUrl, mediaType: query.data.mediaType });
        durations.set(id, query.data.durationMs ?? 0);
        titles.set(id, query.data.title);
        thumbnails.set(id, query.data.thumbnailUrl);
      }
    });
    return { media, durations, titles, thumbnails };
  }, [sourceQueries, sourceIds]);

  /**
   * What a source is called in *this* edit: the person's own label when they
   * gave one, the library title otherwise. This is what the timeline blocks
   * and the panel show — naming footage by its role ("B-roll", "demo take 2")
   * is most of what makes a multi-source edit navigable.
   */
  const displayTitles = useMemo(() => {
    const merged = new Map(sources.titles);
    for (const [id, label] of Object.entries(sourceLabels)) merged.set(id, label);
    return merged;
  }, [sources.titles, sourceLabels]);

  // ── Edits: every mutation records one history step ─────────────────────────
  const commit = useCallback(
    (nextClips: Clip[], nextChapters: DraftChapter[] = chapters) => {
      setHistory((h) => pushHistory(h, { clips, chapters }));
      setClips(nextClips);
      setChapters(nextChapters);
    },
    [clips, chapters],
  );

  const commitChapters = useCallback(
    (next: DraftChapter[]) => {
      setHistory((h) => pushHistory(h, { clips, chapters }));
      setChapters(next);
    },
    [clips, chapters],
  );

  const handleUndo = useCallback(() => {
    const result = undoOp(history, { clips, chapters });
    if (!result) return;
    setHistory(result.history);
    setClips(result.snapshot.clips);
    setChapters(result.snapshot.chapters as DraftChapter[]);
  }, [history, clips, chapters]);

  const handleRedo = useCallback(() => {
    const result = redoOp(history, { clips, chapters });
    if (!result) return;
    setHistory(result.history);
    setClips(result.snapshot.clips);
    setChapters(result.snapshot.chapters as DraftChapter[]);
  }, [history, clips, chapters]);

  const seek = useCallback((ms: number) => {
    setPlayheadMs(ms);
    previewRef.current?.seekToOutput(ms);
  }, []);

  /**
   * Split, honestly. A refused split (playhead on a cut, or within 100ms of
   * one) used to both push a phantom undo step and say nothing — a key that
   * silently does nothing reads as broken, and Ctrl+Z then undid... nothing.
   */
  const trySplit = useCallback(() => {
    const next = splitAt(clips, playheadMs);
    if (next !== clips) {
      commit(next);
    } else if (clips.length > 0) {
      toast(t("editor.splitNothing"), "success");
    }
  }, [clips, playheadMs, commit, t]);

  const save = useMutation({
    mutationFn: () =>
      api.saveStudioProject(projectId, {
        title,
        timeline: { version: 1, clips } satisfies TimelineDoc,
        draftChapters: chapters,
        sourceLabels,
      }),
    onSuccess: () => {
      setSavedFingerprint(fingerprint);
      void queryClient.invalidateQueries({ queryKey: ["studio-projects"] });
    },
  });

  const publish = useMutation({
    mutationFn: async (input: Parameters<typeof api.publishStudioProject>[1]) => {
      // Publish always publishes what is on screen, so save first — publishing
      // a stale draft while looking at a fresh one is the worst surprise here.
      await api.saveStudioProject(projectId, {
        title,
        timeline: { version: 1, clips } satisfies TimelineDoc,
        draftChapters: chapters,
        sourceLabels,
      });
      setSavedFingerprint(fingerprint);
      return api.publishStudioProject(projectId, input);
    },
    onSuccess: (result) => {
      setPublishing(false);
      toast(
        result.mode === "chapters"
          ? t("editor.publishedChapters")
          : t("editor.publishedRender"),
        "success",
      );
      void navigate({ to: "/r/$recordingId", params: { recordingId: result.recordingId } });
    },
  });

  // ── Keyboard: the gestures that make the editor fast ───────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const mod = e.ctrlKey || e.metaKey;

      // Save works from anywhere, including mid-typing in a chapter title —
      // that is exactly when people reach for it. The single-letter shortcuts
      // below still defer to text fields.
      if (mod && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (dirty && !save.isPending) save.mutate();
        return;
      }

      if (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      // A modal owns the keyboard while it is open — S must not split a clip
      // underneath the publish dialog or the library picker.
      if (publishing || pickerOpen) return;

      if (mod && e.key.toLowerCase() === "z" && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      } else if (mod && e.key.toLowerCase() === "z") {
        e.preventDefault();
        handleUndo();
      } else if (e.key.toLowerCase() === "s") {
        trySplit();
      } else if (e.key.toLowerCase() === "d" && selectedId) {
        commit(duplicateClip(clips, selectedId));
      } else if (e.key.toLowerCase() === "m" && selectedId) {
        commit(toggleMute(clips, selectedId));
      } else if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        commit(removeClip(clips, selectedId));
        setSelectedId(null);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [clips, playheadMs, selectedId, dirty, save, commit, handleUndo, handleRedo, publishing, pickerOpen, trySplit]);


  if (project.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="aspect-video w-full max-w-3xl rounded-xl" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (project.isError || !project.data) {
    return (
      <ErrorState
        title={t("editor.openErrorTitle")}
        error={t("editor.openErrorBody")}
      />
    );
  }

  const durationMs = totalDurationMs(clips);
  const sourceRecordingId = project.data.sourceRecordingId;
  const identity =
    sourceRecordingId !== null &&
    isIdentityTimeline(
      { version: 1, clips },
      {
        recordingId: sourceRecordingId,
        durationMs: sources.durations.get(sourceRecordingId) ?? 0,
      },
    );

  const selectedClip = selectedId ? clips.find((c) => c.id === selectedId) : undefined;
  const hasTransitions = clips.some((c, i) => i > 0 && c.transitionIn);

  return (
    <div
      className={cn(
        "flex flex-col gap-3",
        // Fit mode: the editor owns exactly the viewport below the app header
        // (h-14) and the page paddings, so nothing ever scrolls. Large screens
        // only — below lg the grid stacks and pinning the height would make
        // the columns overlap the timeline instead of removing a scrollbar.
        fitScreen && "lg:h-[calc(100dvh-7.5rem)]",
      )}
    >
      {/* Header: title, dirty state, save, publish. */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => void navigate({ to: "/studio" })}>
            <ArrowLeft size={14} />
          </Button>
          <h1 className="min-w-0 text-[16px] font-semibold text-text">
            <InlineRename
              value={title || t("editor.untitled")}
              label={t("editor.rename")}
              onRename={(next) => setTitle(next)}
              inputClassName="w-64"
            />
          </h1>
          {dirty ? (
            <span className="text-[11.5px] text-warning" title={t("editor.unsavedTitle")}>
              {t("editor.unsaved")}
            </span>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            // The mode it toggles only exists on large screens; showing a
            // control that visibly does nothing is worse than not showing it.
            className="hidden lg:inline-flex"
            onClick={() => {
              const next = !fitScreen;
              setFitScreen(next);
              localStorage.setItem("studio.fitScreen", next ? "1" : "0");
            }}
            title={
              fitScreen
                ? t("editor.fitScreenOff")
                : t("editor.fitScreenOn")
            }
          >
            {fitScreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUndo}
            disabled={history.past.length === 0}
            title={t("editor.undo")}
          >
            <Undo2 size={14} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRedo}
            disabled={history.future.length === 0}
            title={t("editor.redo")}
          >
            <Redo2 size={14} />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
            title={t("editor.saveDraft")}
          >
            {save.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Save size={13} />
            )}
            {t("common:actions.save")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={clips.length === 0 || publish.isPending}
            onClick={() => setPublishing(true)}
          >
            <UploadCloud size={13} />
            {t("editor.publish")}
          </Button>
        </div>
      </div>

      {/* Preview beside the side panel; the timeline runs full-width below
          both, so the track goes edge to edge and the panel ends above it. */}
      <div
        className={cn(
          "grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]",
          fitScreen && "lg:min-h-0 lg:flex-1",
        )}
      >
        {/* Left: preview + toolbar. */}
        <div className="flex min-h-0 min-w-0 flex-col gap-3">
          <div className={cn("min-h-0", fitScreen && "lg:flex-1")}>
            {fitScreen ? (
              <div className="mx-auto aspect-video w-full max-w-full lg:h-full lg:w-auto">
                <StudioPreview
                  ref={previewRef}
                  clips={clips}
                  sources={sources.media}
                  onOutputTime={setPlayheadMs}
                  fill
                />
              </div>
            ) : (
              <StudioPreview
                ref={previewRef}
                clips={clips}
                sources={sources.media}
                onOutputTime={setPlayheadMs}
              />
            )}
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              onClick={trySplit}
              disabled={clips.length === 0}
              title={t("editor.splitTitle")}
            >
              <Scissors size={13} />
              {t("editor.split")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={!selectedId}
              onClick={() => selectedId && commit(duplicateClip(clips, selectedId))}
              title={t("editor.duplicateTitle")}
            >
              <Copy size={13} />
              {t("editor.duplicate")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={!selectedId}
              onClick={() => selectedId && commit(toggleMute(clips, selectedId))}
              title={
                selectedClip?.muted
                  ? t("editor.unmuteTitle")
                  : t("editor.muteTitle")
              }
            >
              <VolumeX size={13} />
              {selectedClip?.muted ? t("editor.unmute") : t("editor.mute")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={!selectedId}
              onClick={() => {
                if (!selectedId) return;
                commit(removeClip(clips, selectedId));
                setSelectedId(null);
              }}
              title={t("editor.removeTitle")}
            >
              <Trash2 size={13} />
              {t("common:actions.remove")}
            </Button>
            {/* Keyboard-and-mouse instructions, for screens that have them. */}
            <span className="ml-auto hidden text-[11.5px] text-text-faint lg:inline">
              {hasTransitions
                ? t("editor.hintTransitions")
                : t("editor.hintKeys")}
            </span>
          </div>
        </div>

        {/* Right: sources / chapters. Absolutely sized to the row, so it can
            never grow past the preview column and push the timeline down. */}
        <div className="relative h-[min(28rem,60dvh)] lg:h-auto">
          <Card className="absolute inset-0 flex flex-col overflow-hidden">
          <div className="flex shrink-0 gap-1 border-b border-border p-1.5">
            {(["sources", "chapters"] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setPanel(tab)}
                className={cn(
                  "flex-1 rounded-md px-3 py-1.5 text-[13px] font-medium capitalize transition-colors",
                  panel === tab
                    ? "bg-surface-hover text-text"
                    : "text-text-muted hover:text-text",
                )}
              >
                {t(`editor.tabs.${tab}`)}
              </button>
            ))}
          </div>

          {panel === "chapters" ? (
            <ChapterEditor
              chapters={chapters}
              playheadMs={playheadMs}
              durationMs={durationMs}
              onChange={commitChapters}
              onSeek={seek}
            />
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="shrink-0 space-y-2 border-b border-border p-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full"
                  onClick={() => setPickerOpen(true)}
                  title={t("editor.browseLibraryTitle")}
                >
                  <LibraryBig size={13} />
                  {t("editor.browseLibrary")}
                </Button>
                <div className="flex flex-wrap items-center gap-2">
                  <UploadButton projectId={null} compact />
                  <ImportLinkButton projectId={null} compact />
                </div>
                <p className="text-[11px] leading-relaxed text-text-faint">
                  {t("editor.uploadsNote")}
                </p>
              </div>

              {/* The timeline as a list: one row per clip, in playback order.
                  A cut shows up as two rows the moment it happens, and rows
                  drag to reorder — the same operation as dragging blocks on
                  the strip, in a form that needs no pixel aim. */}
              <div className="min-h-0 flex-1 overflow-y-auto p-2">
                <ElementList
                  clips={clips}
                  displayTitles={displayTitles}
                  thumbnails={sources.thumbnails}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  onSeek={seek}
                  onReorder={(from, to) => {
                    const next = moveClip(clips, from, to);
                    // Same reference = the drag went nowhere; no history step.
                    if (next !== clips) commit(next);
                  }}
                  // A rename is an edit of the timeline document — committed,
                  // so it saves with the draft and undoes like any other edit.
                  onRenameClip={(clipId, name) =>
                    commit(setClipLabel(clips, clipId, name))
                  }
                  sourceDurations={sources.durations}
                  onTrim={(clipId, edge, sourceMs) => {
                    const clip = clips.find((c) => c.id === clipId);
                    if (!clip) return;
                    commit(
                      trimClip(
                        clips,
                        clipId,
                        edge,
                        sourceMs,
                        sources.durations.get(clip.recordingId) ?? clip.sourceEndMs,
                      ),
                    );
                  }}
                />
              </div>
            </div>
          )}
          </Card>
        </div>
      </div>

      {/* The timeline: full width, under both columns. */}
      <div className="shrink-0">
        <Timeline
          clips={clips}
          sourceDurations={sources.durations}
          sourceTitles={displayTitles}
          playheadMs={playheadMs}
          selectedId={selectedId}
          chapterStarts={chapters.map((c) => ({ startMs: c.startMs, title: c.title }))}
          onSelect={setSelectedId}
          onChange={(next) => commit(next)}
          onSeek={seek}
          onToggleMute={(clipId) => commit(toggleMute(clips, clipId))}
          onSetTransition={(clipId, transition) =>
            commit(setTransition(clips, clipId, transition))
          }
        />
      </div>

      {pickerOpen ? (
        <LibraryPicker
          addedIds={new Set(clips.map((c) => c.recordingId))}
          onAdd={(recording) =>
            commit(appendClip(clips, recording.id, recording.durationMs ?? 1000))
          }
          onClose={() => setPickerOpen(false)}
        />
      ) : null}

      {publishing ? (
        <PublishDialog
          defaultTitle={title || t("editor.untitled")}
          isIdentity={identity}
          hasChapters={chapters.length > 0}
          busy={publish.isPending}
          onPublish={(input) => publish.mutate(input)}
          onClose={() => setPublishing(false)}
        />
      ) : null}
    </div>
  );
}
